import React, { useState } from "react";
import { useSelector } from "react-redux";

import {
  Container,
  CircularProgress,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  Stack,
  Alert,
} from "@mui/material";
import {contactSupport} from "../../_DatabaseFunctions/cloudCalls/emailNotifications";
import { RootState } from "types";

interface ContactFormState {
  successMsg: string;
  name: string;
  selection: string;
  message: string;
}

function ContactForm() {
  const user = useSelector((state: RootState) => state.user.user);

  const [values, setValues] = useState<ContactFormState>({
    successMsg: "",
    name: "",
    selection: "Bug Report",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    if (values.name === "") {
      setValues({ ...values, successMsg: "Name Required" });
      setIsLoading(false);
    } else if (values.message === "") {
      setValues({ ...values, successMsg: "Message Required" });
      setIsLoading(false);
    } else {
      try {
        // Create clean data object to avoid any potential circular references
        const cleanData = {
          name: String(values.name),
          selection: String(values.selection),
          message: String(values.message),
          userEmail: String(user?.email || ""),
        };
        
        console.log("Contacting support with data:", cleanData);
        await contactSupport(cleanData);
        
        setValues({
          successMsg: "Message Sent. Thank you for your input",
          name: "",
          selection: "Bug Report",
          message: "",
        });
      } catch (error) {
        console.error("Contact form error:", error);
        setValues({ ...values, successMsg: "Error sending message" });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
  };

  return (
    <Box
      sx={{
        py: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <form onSubmit={handleSubmit} style={{ width: "100%" }}>
        <Stack spacing={3} sx={{ maxWidth: 500, width: "100%", mx: "auto" }}>
          <TextField
            name="name"
            value={values.name}
            onChange={handleInputChange}
            placeholder="Name"
            label="Name"
            variant="outlined"
            fullWidth
            size="medium"
          />

          <FormControl fullWidth size="medium">
            <InputLabel>Type</InputLabel>
            <Select
              name="selection"
              value={values.selection}
              onChange={handleInputChange}
              label="Type"
            >
              <MenuItem value="Bug Report">Bug Report</MenuItem>
              <MenuItem value="User Report">User Report</MenuItem>
              <MenuItem value="Feedback">Feedback</MenuItem>
              <MenuItem value="Feature Request">Feature Request</MenuItem>
            </Select>
          </FormControl>

          <TextField
            name="message"
            value={values.message}
            onChange={handleInputChange}
            placeholder="Message"
            label="Message"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            size="medium"
          />

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            {isLoading ? (
              <CircularProgress color="secondary" />
            ) : (
              <Button
                variant="contained"
                color="primary"
                type="submit"
                size="large"
                sx={{
                  minWidth: 150,
                  py: 1.5,
                  fontSize: "1.1rem",
                  borderRadius: 2,
                }}
              >
                Submit
              </Button>
            )}
          </Box>

          {values.successMsg && (
            <Alert
              severity={
                values.successMsg.includes("Message Sent") ? "success" : "error"
              }
              sx={{ mt: 2, textAlign: "center" }}
            >
              {values.successMsg}
            </Alert>
          )}
        </Stack>
      </form>
    </Box>
  );
}

export default ContactForm;
